import { useRef, useState } from "react";
import { Bold, Italic, Strikethrough, Underline } from "lucide-react";
import {
  DEFAULT_FORWARDING_SETTINGS,
  MAX_TEMPLATE_LENGTH,
  TEMPLATE_VARIABLES,
  type TemplateVariable,
} from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { buildPreviewHtml } from "@/lib/forwarding";
import { MessagePreview } from "@/components/forwarding/MessagePreview";
import { InlineError } from "@/components/forwarding/controls";

const VARIABLE_LABELS: Readonly<Record<TemplateVariable, string>> = {
  title: "Título",
  priceText: "Texto do preço",
  price: "Preço",
  discountedPrice: "Preço com desconto",
  originalPrice: "Preço original",
  discountPercent: "Desconto %",
  savings: "Economia",
  coupon: "Cupom",
  affiliateUrl: "Link",
  provider: "Loja",
};

const MARKS = [
  { mark: "**", label: "Negrito", icon: Bold },
  { mark: "_", label: "Itálico", icon: Italic },
  { mark: "~~", label: "Riscado", icon: Strikethrough },
  { mark: "__", label: "Sublinhado (só Telegram)", icon: Underline },
] as const;

interface TemplateEditorProps {
  readonly initialName: string;
  readonly initialBody: string;
  readonly saving: boolean;
  readonly error: string | null;
  readonly onSave: (name: string, body: string) => void;
  readonly onCancel: () => void;
}

type Transform = (before: string, selected: string, after: string) => [string, number];

/** Estado do corpo do template + edicoes na posicao do cursor (variavel e formatacao). */
function useTemplateBody(initialBody: string) {
  const [body, setBody] = useState(initialBody);
  const ref = useRef<HTMLTextAreaElement>(null);

  const edit = (transform: Transform): void => {
    const el = ref.current;
    const start = el?.selectionStart ?? body.length;
    const end = el?.selectionEnd ?? body.length;
    const [next, cursor] = transform(body.slice(0, start), body.slice(start, end), body.slice(end));
    setBody(next.slice(0, MAX_TEMPLATE_LENGTH));
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(cursor, cursor);
    });
  };
  const insertVariable = (v: TemplateVariable): void =>
    edit((b, _selected, a) => [`${b}{${v}}${a}`, b.length + v.length + 2]);
  const wrap = (mark: string): void =>
    edit((b, s, a) => [
      `${b}${mark}${s || "texto"}${mark}${a}`,
      b.length + mark.length * 2 + (s || "texto").length,
    ]);
  return { body, setBody, ref, insertVariable, wrap };
}

function NameField({
  name,
  onChange,
}: {
  name: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <div className="space-y-1.5">
      <label htmlFor="tpl-name" className="text-xs font-medium text-foreground">
        Nome do template
      </label>
      <Input
        id="tpl-name"
        maxLength={60}
        value={name}
        onChange={(e) => onChange(e.target.value)}
        placeholder="ex.: Oferta relâmpago"
      />
    </div>
  );
}

function FormatToolbar({ onWrap }: { onWrap: (mark: string) => void }): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-1" role="toolbar" aria-label="Formatação">
      {MARKS.map(({ mark, label, icon: Icon }) => (
        <Button
          key={mark}
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={label}
          title={label}
          onClick={() => onWrap(mark)}
        >
          <Icon size={14} />
        </Button>
      ))}
    </div>
  );
}

function BodyCounter({ length }: { length: number }): JSX.Element {
  return (
    <div className="flex items-center justify-between text-[11px] text-muted-foreground">
      <span>Linha com variável vazia (ex.: sem cupom) some da mensagem.</span>
      <span className="font-mono tabular-nums">
        {length}/{MAX_TEMPLATE_LENGTH}
      </span>
    </div>
  );
}

function VariablePicker({ onInsert }: { onInsert: (v: TemplateVariable) => void }): JSX.Element {
  return (
    <div>
      <p className="label-editorial mb-2">Variáveis</p>
      <div className="flex flex-wrap gap-1.5">
        {TEMPLATE_VARIABLES.map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => onInsert(v)}
            className="rounded-md border border-border bg-[hsl(var(--surface-raised))] px-2 py-1 font-mono text-[11px] text-foreground transition-colors hover:border-primary/40 hover:text-primary"
            title={VARIABLE_LABELS[v]}
          >
            {`{${v}}`}
          </button>
        ))}
      </div>
    </div>
  );
}

function PreviewPane({ body }: { body: string }): JSX.Element {
  const html = buildPreviewHtml(DEFAULT_FORWARDING_SETTINGS, [], "shopee", body);
  return (
    <aside
      className="space-y-3 rounded-xl border border-dashed border-border bg-[hsl(var(--surface)/0.5)] p-3"
      aria-label="Prévia"
    >
      <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        Telegram
      </p>
      <MessagePreview html={html} platform="telegram" />
      <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        WhatsApp
      </p>
      <MessagePreview html={html} platform="whatsapp" />
    </aside>
  );
}

/** Editor de template com variaveis clicaveis, formatacao e previa ao vivo. */
export function TemplateEditor({
  initialName,
  initialBody,
  saving,
  error,
  onSave,
  onCancel,
}: TemplateEditorProps): JSX.Element {
  const [name, setName] = useState(initialName);
  const { body, setBody, ref, insertVariable, wrap } = useTemplateBody(initialBody);
  // H2: sem {affiliateUrl} a oferta sairia sem link (o banco tambem recusa).
  const hasLink = body.includes("{affiliateUrl}");
  const valid =
    name.trim().length >= 1 && name.trim().length <= 60 && body.trim().length > 0 && hasLink;

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_minmax(0,20rem)]">
      <div className="space-y-3">
        <NameField name={name} onChange={setName} />
        <FormatToolbar onWrap={wrap} />
        <textarea
          ref={ref}
          aria-label="Texto do template"
          rows={9}
          maxLength={MAX_TEMPLATE_LENGTH}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="w-full resize-y rounded-md border border-border bg-background px-3 py-2 font-mono text-sm text-foreground outline-none focus:border-primary/50"
        />
        <BodyCounter length={body.length} />
        <VariablePicker onInsert={insertVariable} />
        {!hasLink && (
          <p role="alert" className="text-xs text-amber-300">
            Inclua a variável {"{affiliateUrl}"}: é ela que leva o seu link de afiliado.
          </p>
        )}
        <InlineError message={error} />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
          <Button disabled={!valid || saving} onClick={() => onSave(name.trim(), body)}>
            {saving ? "Salvando..." : "Salvar template"}
          </Button>
        </div>
      </div>
      <PreviewPane body={body} />
    </div>
  );
}
